<template>
  <div id="ClientWelcomePage" class="grey darken-4 main-content">
    <v-main>
      <v-container fixed>
        <v-layout row wrap justify-center>
          <v-flex
            v-if="currentUser.roles.includes('client-manager') === true || currentUser.num_clients > 1"
            xs12
            md6
            class="pa-12"
          >
            <h2 class="whitetext mb-4">Welcome to PIXL8</h2>
            <p class="whitetext">
              Please select a client from the list to continue.
            </p>
            <v-form>
              <v-autocomplete
                v-model="expClientModel"
                :items="clientList"
                :loading="isLoading || isLoadingMore"
                :search-input.sync="search"
                :no-data-text="noDataText"
                filled
                dense
                solo
                flat
                background-color="white"
                item-text="name"
                item-value="id"
                label="Client"
                class="client-list"
                prepend-icon="mdi-database-search"
                return-object
                @update:search="handleSearch"
                ref="autocomplete"
              >
                <template v-slot:append-item>
                  <div
                    v-intersect="{
                      handler: endIntersect,
                      options: {
                        threshold: 0.5,
                        rootMargin: '0px'
                      }
                    }"
                    style="height: 1px"
                  ></div>
                </template>
                <template v-slot:append>
                  <v-progress-circular
                    v-if="isLoading || isLoadingMore"
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </template>
              </v-autocomplete>

              <v-btn
                right
                color="primary"
                class="mt-2"
                @click="setActiveExpClient()"
              >
                Continue
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-form>
          </v-flex>
          <create-exp-client-user />
        </v-layout>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import store from "@/store/";
import {
  FETCH_EXPCLIENT_LIST,
  SET_ACTIVE_EXPCLIENT,
} from "@/store/actions.type";

export default {
  name: "AMDashboardPage",
  data: () => ({
    descriptionLimit: 60,
    entries: [],
    isLoading: false,
    isLoadingMore: false,
    search: null,
    expClientModel: null,
    searchTimeout: null,
    noDataText: "No clients found",
    currentPage: 1,
    lastLoadTime: 0,
    minLoadDelay: 500, // Minimum delay between loads in ms
    intersectionDebounceTimeout: null
  }),
  computed: {
    clientList() {
      if (!this.expClientList) return [];
      return this.expClientList;
    },
    ...mapGetters([
      "isAuthenticated",
      "currentUser",
      "activeExpClient",
      "expClientList",
      "hasMorePages"
    ]),
  },

  watch: {
    search(newVal) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadClients(1, newVal);
      }, 300);
    }
  },
  mounted() {
    if (this.currentUser.roles.includes("client-manager") === true || this.currentUser.num_clients > 1) {
      this.loadClients();
    }
  },
  methods: {
    async loadClients(page = 1, search = '') {
      // Use appropriate loading state based on whether this is initial load or loading more
      const isInitialLoad = page === 1;
      const loadingState = isInitialLoad ? 'isLoading' : 'isLoadingMore';

      // Check if we're already loading
      if (this.isLoading || this.isLoadingMore) return;

      // Set appropriate loading state
      this[loadingState] = true;

      try {
        await store.dispatch(FETCH_EXPCLIENT_LIST, {
          userId: this.currentUser.id,
          page,
          search,
          append: page > 1
        });
        if (this.activeExpClient) this.expClientModel = this.activeExpClient;
        this.lastLoadTime = Date.now();
      } catch (error) {
        console.error('Error loading clients:', error);
        this.noDataText = "Error loading clients";
      } finally {
        // Clear loading state
        this[loadingState] = false;
      }
    },
    handleSearch(val) {
      this.search = val;
    },
    endIntersect(isIntersecting) {
      // Clear any existing debounce timeout
      if (this.intersectionDebounceTimeout) {
        clearTimeout(this.intersectionDebounceTimeout);
      }

      // Debounce the intersection callback
      this.intersectionDebounceTimeout = setTimeout(() => {
        if (!isIntersecting) return;

        // Check if we can load more
        if (!this.hasMorePages || this.isLoading || this.isLoadingMore) return;

        // Check minimum delay between loads
        const timeSinceLastLoad = Date.now() - this.lastLoadTime;
        if (timeSinceLastLoad < this.minLoadDelay) return;

        console.log('Intersection detected, loading more clients');
        this.currentPage++;
        this.loadClients(this.currentPage, this.search);
      }, 100); // 100ms debounce
    },
    setActiveExpClient() {
      store.dispatch(SET_ACTIVE_EXPCLIENT, this.expClientModel);
      // Go to photo search
      this.$router.push({
        name: "imagesPage",
        params: { clientId: this.expClientModel.id },
      });
    },
  },
};
</script>

<style scoped>
.client-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
